import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FormControl, InputGroup, Spinner, ListGroup, Modal, Button, Image } from 'react-bootstrap';
import { Cnt } from '../Cnt.ThreeDeeCrate.SDK/Cnt.ThreeDeeCrate.SDK';
import { AppSettings } from '../AppSettings';
import { ModelModalContent } from './ModelModalContent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useParams } from 'react-router-dom';

interface Props {
  /** Optional: pass computed style from parent. If omitted, will read from URL (/:style/:categoryPath). */
  styleFilter?: Cnt.ThreeDeeCrate.SDK.GraphicalStyleType;
}

export const ModelSearch: React.FC<Props> = ({ styleFilter }) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Cnt.ThreeDeeCrate.SDK.ContainerDTO[]>([]);
  const [selectedModel, setSelectedModel] = useState<Cnt.ThreeDeeCrate.SDK.ContainerModelDTO | null>(null);
  const [showModal, setShowModal] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const skipNextDebounce = useRef(false);

  const triggerSearch = async (input: string) => {
    if (!input) return;

    skipNextDebounce.current = true;
    setLoading(true);
    setResults([]);

    try {
      const api = new Cnt.ThreeDeeCrate.SDK.Api(AppSettings.APIUrl);
      const searchRequest = new Cnt.ThreeDeeCrate.SDK.SearchRequestDTO({
        SearchName: input,
        GraphicalStyle: styleFilter, // ✅ style passed to backend
      });
      const allResults = await api.container.Search(searchRequest);
      setResults(allResults.slice(0, 5));
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (!skipNextDebounce.current) {
        setDebouncedQuery(query);
      } else {
        skipNextDebounce.current = false;
      }
    }, 1500);
    return () => clearTimeout(handler);
  }, [query]);

  useEffect(() => {
    if (debouncedQuery) {
      triggerSearch(debouncedQuery);
    }
  }, [debouncedQuery, styleFilter]); // re-run if style changes

  const handleModelClick = async (model: Cnt.ThreeDeeCrate.SDK.ContainerDTO) => {
    try {
      const api = new Cnt.ThreeDeeCrate.SDK.Api(AppSettings.APIUrl);
      const fullModel = await api.model.GetModelInformation(model.Id ?? '');
      setSelectedModel(fullModel);
      setShowModal(true);
    } catch (err) {
      console.error('Failed to fetch full model info:', err);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="model-search-wrapper position-relative" style={{ maxWidth: 600, margin: '0 auto' }}>
      <div ref={wrapperRef} style={{ position: 'relative' }}>
        <InputGroup className="mb-0">
          <FormControl
            placeholder="Search models..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && triggerSearch(query)}
          />
          <Button variant="outline-secondary" onClick={() => triggerSearch(query)}>
            <FontAwesomeIcon icon={faSearch} />
          </Button>
          {loading && (
            <InputGroup.Text>
              <Spinner animation="border" size="sm" />
            </InputGroup.Text>
          )}
        </InputGroup>

        {!loading && results.length > 0 && (
          <ListGroup style={{ position: 'absolute', zIndex: 999, width: '100%', top: '100%' }}>
            {results.map((model) => (
              <ListGroup.Item
                key={model.Id}
                action
                onClick={() => handleModelClick(model)}
                className={`d-flex align-items-center gap-2 ${
                  model.Visibility === Cnt.ThreeDeeCrate.SDK.VisibilityType.Private ? 'text-danger' : ''
                }`}
              >
                <Image
                  src={`https://s3.us-east-2.amazonaws.com/3dcrate.com/uploads/${model.Id}/thumbnail_sm.png`}
                  rounded
                  width={48}
                  height={48}
                  style={{ objectFit: 'cover' }}
                  alt="thumbnail_sm"
                />
                {model.Name ?? 'Unnamed Model'}
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedModel?.Container?.Name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ModelModalContent model={selectedModel} />
        </Modal.Body>
      </Modal>
    </div>
  );
};
